import { Link } from "react-router-dom";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, ArrowRight, Rocket, Target, Layers, Brain, Activity, TrendingUp, AlertCircle, ArrowDown } from "lucide-react";
import { FadeIn, SlideUp } from "@/components/ui/motion-wrappers";
import { IntegrationsSection } from "@/components/IntegrationsSection";
import { FeaturesGrid } from "@/components/shared/FeaturesGrid";
import { SystemLadder } from "@/components/shared/SystemLadder";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";
import { ScaleRoasMockup } from "@/components/shared/ScaleRoasMockup";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const ScaleSystem = () => {

    const features = [
        "Everything in Growth Engine",
        "Google + Meta ads management",
        "Retargeting sequences",
        "Multi-step funnels & split testing",
        "Dynamic funnels for segments",
        "Lead scoring tied to ad events",
        "Advanced attribution models",
        "UTM + pixel architecture",
        "Revenue-based reporting",
        "Creative + messaging guidance",
        "Bi-weekly strategy calls",
        "High-volume usage included"
    ];



    const scrollToLadder = () => {
        const comparisonElement = document.getElementById('ladder');
        if (comparisonElement) {
            comparisonElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const breadcrumbItems = [
        { name: "Home", item: "https://frayze.ca/" },
        { name: "Solutions", item: "https://frayze.ca/solutions/" },
        { name: "Scale System", item: "https://frayze.ca/solutions/ad-management-scale/" }
    ];

    return (
        <div className="min-h-screen bg-background font-sans">
            <SEOHead
                title="Google & Meta Ad Management at Scale | Frayze"
                description="Scale Google & Meta ads without the chaos. Built for trades and service businesses who want every dollar of spend tracked to real revenue."
                canonical="https://frayze.ca/solutions/ad-management-scale/"
                keywords="ad management, paid growth, Google ads management, Meta ads management, PPC for trades, ad spend tracking"
                breadcrumbs={breadcrumbItems}
            />

            <Header />

            <div className="container mx-auto px-6 py-4 absolute top-20 z-20">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/">Home</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/solutions/">Solutions</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage className="text-white/80">Scale System</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <main className="pt-40">
                {/* Hero */}
                <section className="relative bg-gradient-to-br from-[#101319] via-[#101319] to-black text-white py-24 md:py-32 overflow-hidden">
                    <div className="absolute inset-0 bg-grid-white/[0.05] pointer-events-none" />

                    {/* Animated Background Blobs */}
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#2F5FE8]/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3 " />
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#2F5FE8]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3  delay-1000" />

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <div className="text-center lg:text-left">
                                <FadeIn>
                                    <div className="inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#7EA0F5] mb-8">
                                        <div className="w-6 h-[1px] bg-[#2F5FE8]" />
                                        <span>Growth Accelerator for Trades &amp; Service Businesses</span>
                                    </div>
                                </FadeIn>

                                <SlideUp delay={0.1}>
                                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
                                        Scale System
                                        <span className="block mt-1 text-[#2F5FE8]">
                                            Ads Are Gasoline, Not The Engine.
                                        </span>
                                    </h1>
                                </SlideUp>

                                <SlideUp delay={0.2}>
                                    <p className="text-lg md:text-xl text-white/70 mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0">
                                        Pour fuel on a system that already converts. We run your Google &amp; Meta ads and track every dollar straight through to booked revenue.
                                    </p>
                                </SlideUp>

                                <SlideUp delay={0.3}>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                        <Button
                                            variant="cta"
                                            size="lg"
                                            className="text-lg px-8 py-4 font-bold shadow-lg shadow-[#2F5FE8]/25 hover:shadow-[#2F5FE8]/40 transition-all scale-100 hover:scale-105 bg-[#2F5FE8] hover:bg-[#2049BE] text-white rounded-xl"
                                            asChild
                                        >
                                            <Link to="/book-consultation/">
                                                Start Scaling
                                                <ArrowRight className="ml-2 h-5 w-5" />
                                            </Link>
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="lg"
                                            className="bg-white/5 hover:bg-white/10 border-white/20 text-white backdrop-blur-sm"
                                            onClick={scrollToLadder}
                                        >
                                            See System Ladder
                                            <ArrowDown className="ml-2 h-5 w-5" />
                                        </Button>
                                    </div>
                                </SlideUp>
                            </div>

                            <SlideUp delay={0.2}>
                                <div className="flex justify-center lg:justify-end">
                                    <ScaleRoasMockup />
                                </div>
                            </SlideUp>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <FeaturesGrid
                    title="Included in Scale System"
                    subtitle="We track everything from ad click to revenue collection."
                    features={features}
                    theme="blue"
                />

                {/* Detailed Features Section */}
                <section className="py-24 bg-slate-50 dark:bg-slate-900/30">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-16">
                                <SlideUp>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                                        Core Outcomes
                                    </h2>
                                    <p className="text-lg text-muted-foreground">
                                        For businesses already getting steady leads who are ready to grow faster.
                                    </p>
                                </SlideUp>
                            </div>

                            <div className="grid gap-8">
                                <SlideUp delay={0.1}>
                                    <div className="bg-white dark:bg-slate-800 rounded-xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-3 bg-[#2F5FE8]/10 dark:bg-[#2F5FE8]/20 rounded-lg">
                                                <Target className="h-6 w-6 text-[#2F5FE8]" />
                                            </div>
                                            <h3 className="text-xl font-bold">Ads That Bring In Customers</h3>
                                        </div>
                                        <ul className="space-y-4">
                                            <li className="flex gap-3">
                                                <Check className="h-5 w-5 text-[#2F5FE8] shrink-0 mt-0.5" />
                                                <div>
                                                    <span className="font-semibold block">Google + Meta ads management</span>
                                                    <span className="text-muted-foreground text-sm">Full campaign management and optimization</span>
                                                </div>
                                            </li>
                                            <li className="flex gap-3">
                                                <Check className="h-5 w-5 text-[#2F5FE8] shrink-0 mt-0.5" />
                                                <div>
                                                    <span className="font-semibold block">Conversion event setup</span>
                                                    <span className="text-muted-foreground text-sm">Tracking every meaningful action</span>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </SlideUp>

                                <div className="grid md:grid-cols-2 gap-8">
                                    <SlideUp delay={0.2}>
                                        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm h-full">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                                                    <Layers className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                                                </div>
                                                <h3 className="text-xl font-bold">Pages That Convert Better</h3>
                                            </div>
                                            <ul className="space-y-4">
                                                <li className="flex gap-3">
                                                    <Check className="h-5 w-5 text-[#2F5FE8] shrink-0 mt-0.5" />
                                                    <div>
                                                        <span className="font-semibold block">Multi-offer landing pages</span>
                                                        <span className="text-muted-foreground text-sm">Testing different hooks and offers</span>
                                                    </div>
                                                </li>
                                                <li className="flex gap-3">
                                                    <Check className="h-5 w-5 text-[#2F5FE8] shrink-0 mt-0.5" />
                                                    <div>
                                                        <span className="font-semibold block">A/B experiments</span>
                                                        <span className="text-muted-foreground text-sm">Constant iteration for higher conversion</span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </SlideUp>

                                    <SlideUp delay={0.3}>
                                        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm h-full">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="p-3 bg-fuchsia-100 dark:bg-fuchsia-900/30 rounded-lg">
                                                    <Brain className="h-6 w-6 text-fuchsia-600 dark:text-fuchsia-400" />
                                                </div>
                                                <h3 className="text-xl font-bold">AI-Enhanced Lead Quality</h3>
                                            </div>
                                            <ul className="space-y-4">
                                                <li className="flex gap-3">
                                                    <Check className="h-5 w-5 text-[#2F5FE8] shrink-0 mt-0.5" />
                                                    <div>
                                                        <span className="font-semibold block">AI qualification</span>
                                                        <span className="text-muted-foreground text-sm"><Link to="/solutions/ai-strategy-rollout/" className="text-[#2F5FE8] hover:text-[#2049BE] font-semibold underline">AI</Link> qualifies leads before they reach sales</span>
                                                    </div>
                                                </li>
                                                <li className="flex gap-3">
                                                    <Check className="h-5 w-5 text-[#2F5FE8] shrink-0 mt-0.5" />
                                                    <div>
                                                        <span className="font-semibold block">Automated segmentation</span>
                                                        <span className="text-muted-foreground text-sm">Route leads based on intent and behavior</span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </SlideUp>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pulse AI Section */}
                <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
                    {/* Background Elements */}
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-900 to-slate-900 pointer-events-none" />
                    <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none" />

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-4xl mx-auto text-center mb-16">
                            <FadeIn>
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-400/20 text-indigo-300 mb-6">
                                    <Brain className="w-4 h-4" />
                                    <span className="text-sm font-medium">Frayze Pulse AI Included</span>
                                </div>
                                <h2 className="text-3xl md:text-5xl font-bold mb-6">
                                    Your Full Intelligence Layer
                                </h2>
                                <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
                                    Pulse enhances scale by showing what’s actually working and what to double down on.
                                </p>
                            </FadeIn>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                            {/* Feature 1 */}
                            <SlideUp delay={0.1} className="h-full">
                                <Card className="bg-white/5 border-white/10 backdrop-blur-sm h-full hover:bg-white/10 transition-colors">
                                    <CardContent className="p-6">
                                        <div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center mb-4">
                                            <Activity className="w-6 h-6 text-indigo-300" />
                                        </div>
                                        <h3 className="text-xl font-bold mb-2 text-white">Performance Scoring</h3>
                                        <p className="text-slate-400 text-sm">
                                            Track team performance and automated interactions.
                                        </p>
                                    </CardContent>
                                </Card>
                            </SlideUp>

                            {/* Feature 2 */}
                            <SlideUp delay={0.2} className="h-full">
                                <Card className="bg-white/5 border-white/10 backdrop-blur-sm h-full hover:bg-white/10 transition-colors">
                                    <CardContent className="p-6">
                                        <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center mb-4">
                                            <TrendingUp className="w-6 h-6 text-purple-300" />
                                        </div>
                                        <h3 className="text-xl font-bold mb-2 text-white">Lead Quality Indicators</h3>
                                        <p className="text-slate-400 text-sm">
                                            Know which ad channels are driving the best leads.
                                        </p>
                                    </CardContent>
                                </Card>
                            </SlideUp>

                            {/* Feature 3 */}
                            <SlideUp delay={0.3} className="h-full">
                                <Card className="bg-white/5 border-white/10 backdrop-blur-sm h-full hover:bg-white/10 transition-colors">
                                    <CardContent className="p-6">
                                        <div className="w-12 h-12 rounded-lg bg-rose-500/20 flex items-center justify-center mb-4">
                                            <AlertCircle className="w-6 h-6 text-rose-300" />
                                        </div>
                                        <h3 className="text-xl font-bold mb-2 text-white">Funnel Health Alerts</h3>
                                        <p className="text-slate-400 text-sm">
                                            Get notified immediately if conversion rates drop.
                                        </p>
                                    </CardContent>
                                </Card>
                            </SlideUp>
                        </div>
                    </div>
                </section>

                {/* System Ladder Section */}
                <section id="ladder" className="py-24 bg-slate-50 dark:bg-slate-900/50">
                    <SystemLadder activeTier="scale" />
                </section>

                <ServiceFAQ
                    title="Common Questions About Scaling"
                    description="Clarity before velocity."
                    items={[
                        {
                            question: "What is the minimum ad spend required?",
                            answer: "We typically recommend a minimum ad spend of $3,000/month for the Scale System. Below this threshold, the algorithms struggle to exit the learning phase and optimize effectively. If you aren't there yet, our Growth Engine is the better starting point."
                        },
                        {
                            question: "Do I own my ad account?",
                            answer: "Yes. Always. Unlike many agencies that hold accounts hostage, we build everything inside YOUR Business Manager. You retain all data, pixel history, and creative assets, even if you stop working with us."
                        },
                        {
                            question: "How is this different from 'Boosting' posts?",
                            answer: "Boosting is for vanity metrics (likes/views). The Scale System builds complex conversion funnels using server-side tracking (CAPI), custom audiences, and retargeting layers designed purely for Revenue and ROAS, not just 'engagement'."
                        },
                        {
                            question: "Does this include creative work?",
                            answer: "Yes. We seek out and design high-performance ad creative (images/video hooks) and write all conversion copy. We also provide scripts for you to record authentic founder-led video ads."
                        }
                    ]}
                />

                <IntegrationsSection />

                {/* Industries That Scale */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-16 max-w-3xl mx-auto">
                            <SlideUp>
                                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                                    Industries Running Scale System
                                </h2>
                                <p className="text-lg text-muted-foreground">
                                    Paid growth works best when <Link to="/solutions/lead-generation-system/" className="text-amber-700 hover:text-amber-800 font-semibold underline">the engine underneath</Link> is already converting.
                                </p>
                            </SlideUp>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            <SlideUp delay={0.1}>
                                <Link to="/automotive/" className="group p-6 border rounded-xl hover:border-[#2F5FE8] hover:shadow-md transition-all block h-full">
                                    <h3 className="font-semibold text-lg mb-2 group-hover:text-[#2F5FE8] transition-colors">Automotive</h3>
                                    <p className="text-sm text-muted-foreground">Inventory ads, retargeting, and attribution tied to sold units.</p>
                                </Link>
                            </SlideUp>
                            <SlideUp delay={0.2}>
                                <Link to="/industries/trades/" className="group p-6 border rounded-xl hover:border-[#2F5FE8] hover:shadow-md transition-all block h-full">
                                    <h3 className="font-semibold text-lg mb-2 group-hover:text-amber-600 transition-colors">Trades & Contractors</h3>
                                    <p className="text-sm text-muted-foreground">Geo-targeted campaigns that fill calendars in specific service areas.</p>
                                </Link>
                            </SlideUp>
                            <SlideUp delay={0.3}>
                                <Link to="/solutions/" className="group p-6 border rounded-xl hover:border-[#2F5FE8] hover:shadow-md transition-all block h-full">
                                    <h3 className="font-semibold text-lg mb-2 group-hover:text-amber-600 transition-colors">Service Businesses</h3>
                                    <p className="text-sm text-muted-foreground">Booking-focused funnels for med spas, dental, fitness, and professional services.</p>
                                </Link>
                            </SlideUp>
                        </div>
                    </div>
                </section>

                {/* Proof Link */}
                <section className="py-16 bg-slate-50 dark:bg-slate-900/30 border-y">
                    <div className="container mx-auto px-6 max-w-5xl">
                        <SlideUp>
                            <div className="text-center mb-10">
                                <p className="text-sm font-bold text-[#2F5FE8] uppercase tracking-widest mb-2">Proven Results</p>
                                <h3 className="text-2xl md:text-3xl font-bold">Scaling Campaign Proof Across Sectors</h3>
                            </div>
                        </SlideUp>
                        <div className="grid md:grid-cols-2 gap-8">
                            <SlideUp>
                                <Link to="/case-studies/need-a-car-automotive-growth/" className="block group hover:bg-zinc-100 dark:hover:bg-slate-800/50 p-8 rounded-2xl transition-colors border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 h-full flex flex-col justify-between cursor-pointer">
                                    <div>
                                        <blockquote className="text-base text-muted-foreground italic mb-6 group-hover:text-foreground transition-colors leading-relaxed">
                                            "Our ad spend ROI tripled once we layered campaigns on top of the Frayze Growth Engine. The system was already converting. Ads just accelerated it."
                                        </blockquote>
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-foreground">Need A Car</div>
                                        <div className="text-sm text-muted-foreground mb-4">Automotive & Dealerships · Ontario</div>
                                        <span className="text-sm font-semibold text-[#2F5FE8] group-hover:text-[#2049BE] inline-flex items-center gap-1">
                                            Read Case Study <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </Link>
                            </SlideUp>

                            <SlideUp delay={0.1}>
                                <Link to="/case-studies/global-aluminum-lead-generation/" className="block group hover:bg-zinc-100 dark:hover:bg-slate-800/50 p-8 rounded-2xl transition-colors border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 h-full flex flex-col justify-between cursor-pointer">
                                    <div>
                                        <blockquote className="text-base text-muted-foreground italic mb-6 group-hover:text-foreground transition-colors leading-relaxed">
                                            Lead-to-booked-quote rate nearly tripled (from about 12% to 34%) after Frayze rebuilt the site, the CRM intake, and the ad strategy. Every number pulled from the systems Frayze runs.
                                        </blockquote>
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-foreground">Global Aluminum Solutions</div>
                                        <div className="text-sm text-muted-foreground mb-4">Trades & Custom Railings · GTA</div>
                                        <span className="text-sm font-semibold text-[#2F5FE8] group-hover:text-[#2049BE] inline-flex items-center gap-1">
                                            Read Case Study <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </Link>
                            </SlideUp>
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6 text-center max-w-2xl">
                        <SlideUp>
                            <h2 className="text-3xl font-bold mb-4">Ready To Accelerate?</h2>
                            <p className="text-muted-foreground mb-12">
                                Book a free strategy session and we&rsquo;ll show you exactly where ad spend can grow your revenue, and whether you&rsquo;re ready to scale.
                            </p>
                            <Button
                                size="lg"
                                className="w-full sm:w-auto bg-[#2F5FE8] hover:bg-[#2049BE] text-white text-lg py-6 rounded-xl shadow-lg shadow-[#2F5FE8]/25"
                                asChild
                            >
                                <Link to="/book-consultation/">
                                    Book My Free Strategy Session
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Link>
                            </Button>
                        </SlideUp>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ScaleSystem;

